import React, { useRef } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Button, TextField, Stack, IconButton, Icons, DateTimeStandard } from '@curves/ui-base';
import { GradientColorHeader, ReadTextField, ProductInfo } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useClubStockOutApi from "@apis/useClubStockOutApi";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import { SkuAddToList, InventoryProductList, InputGroup } from "@clubInventory/components/";
import { useClaimSkuAdd } from "@clubInventory/hooks/useSkuAddToList";
import { ExpectedQuantityCell, getExpectedQuantityCellClassName } from "@clubInventory/purchase/components/";
import { callRef, isFunction, refIsRequiredError } from "@util/utils";
import { parseDate, getNowTime } from "@util/moment";

// 功能：商品領用清單欄位定義
const productClaimHeaderRow = [
	{
		cellKey: "productInfo",
		headerLabel: "商品資訊",
		minWidth: 200,
		formatCell: (_, row) => (
			<ProductInfo
				product={{
					name: row.productName,
					sku: row.sku,
					spec: row.variantName,
					imageUrl: row.pictureSrc,
				}}
				className=""
				showEditIcon={false}
				onEdit={() => {}}
			/>
		),
	},
	{
		cellKey: "claimQuantity",
		headerLabel: "領用數量",
		align: "center",
		minWidth: 120,
		getCellClassName: (claimQuantity, row) => {
			const qty = claimQuantity ?? row.claimQuantity ?? row.quantity ?? 0;
			const stockQuantity = row?.stockQuantity ?? 0;
			return getExpectedQuantityCellClassName(qty, stockQuantity);
		},
		formatCell: (claimQuantity, row, { onOpenQuantityEdit }) => {
			const qty = claimQuantity ?? row.claimQuantity ?? row.quantity ?? 0;
			const stockQuantity = row?.stockQuantity ?? 0;
			return <ExpectedQuantityCell actualQuantity={qty} expectedQuantity={stockQuantity} item={row} onOpenQuantityEdit={onOpenQuantityEdit} />;
		},
	},
	{
		cellKey: "actions",
		headerLabel: "",
		align: "center",
		minWidth: 80,
		formatCell: (_, row, { onDelete, getProductKey }) => {
			const productKey = getProductKey?.(row) ?? `${row.productID}_${row.variantID ?? ""}`;
			return (
				<IconButton onClick={() => onDelete?.(productKey)} size="small">
					<Icons.Trash />
				</IconButton>
			);
		},
	},
];

// 功能：送出 API payload 的 item 映射（領用單明細）
const toConsumptionItem = p => ({
	productID: p.productID,
	variantID: p.variantID ?? 0,
	quantity: Number(p.claimQuantity) || 0,
});

// 功能：商品領用彈窗（掃碼加入商品、填寫領用原因、送出建立領用單）
const ProductClaimDialog = ({ open, onClose, refresh }) => {
	// 功能：表單欄位 ref（建單人、領用原因）
	const { enqueueSnackbar } = useSnackbar();
	const creatorRef = useRef(null);
	const reasonRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();

	const { getClubInventorySearchStockSingleApi } = useClubInventoryApi();
	const { postClubStockOutConsumptionApi } = useClubStockOutApi();

	// 功能：掃碼/輸入 SKU 加入商品；依 SKU 精準查店鋪一般倉單筆庫存
	const skuAdd = useClaimSkuAdd({
		fetchProductBySku: async sku => {
			const match = await getClubInventorySearchStockSingleApi({ text: sku });
			if (!match) return null;
			return {
				productID: match.productID,
				productName: match.productName,
				variantID: match.variantID,
				variantName: match.variantName,
				sku: match.sku,
				pictureSrc: match.pictureSrc,
				stockQuantity: match.stock,
			};
		},
		getProductKey: p => `${p.productID ?? ""}_${p.variantID ?? ""}`,
	});
	const { products: productList, clearProducts: clearProductList } = skuAdd;

	// 功能：關閉時清空商品列表並呼叫 onClose
	const handleClose = e => {
		clearProductList();
		isFunction(onClose) && onClose(e);
	};

	// 功能：驗證必填與商品數量 → 組 payload 送出建立領用單，成功則刷新並關閉
	const handleSubmit = async e => {
		e.preventDefault();

		const requiredRefs = [creatorRef, reasonRef];
		if (refIsRequiredError(...requiredRefs)) return;

		const reason = String(callRef(reasonRef, "getResult") || "").trim();
		if (!reason) {
			enqueueSnackbar("請填寫領用原因", { variant: "warning" });
			return;
		}
		if (productList.length === 0) {
			enqueueSnackbar("請至少加入一個商品", { variant: "warning" });
			return;
		}
		const hasInvalidQuantity = productList.some(item => (Number(item.claimQuantity) || 0) <= 0);
		if (hasInvalidQuantity) {
			enqueueSnackbar("請確認所有商品的領用數量", { variant: "warning" });
			return;
		}
		const payload = {
			stockOutDate: getNowTime(),
			employeeID: Number(callRef(creatorRef, "getResult") || 0),
			reason,
			items: productList.map(toConsumptionItem),
		};

		const resp = await postClubStockOutConsumptionApi(payload);
		if (resp) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-90rem" }} open={open} onClose={handleClose} fullWidth maxWidth="lg">
			<GradientColorHeader onClose={handleClose}>商品領用</GradientColorHeader>
			<DialogContent className="p-3">
				<Grid container spacing={3}>
					<Grid item xs={4}>
						<Stack spacing={2} direction="column">
							<ReadTextField label="建單日期">{parseDate(getNowTime(), DateTimeStandard.DisplayDateBySlash)}</ReadTextField>
							<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth required />
							<TextField label="原因" ref={reasonRef} required placeholder="請填寫領用原因" multiline minRows={3} fullWidth />
						</Stack>
					</Grid>
					<Grid item xs={8}>
						<SkuAddToList {...skuAdd}>
							<Stack spacing={2} alignItems="center" className="mb-3">
								<InputGroup className="product-input-group">
									<InputGroup.StartAdornment>
										<SkuAddToList.ScanButton />
									</InputGroup.StartAdornment>
									<InputGroup.Input>
										<SkuAddToList.Input placeholder="請使用手持掃描器掃描或輸入商品SKU" />
									</InputGroup.Input>
								</InputGroup>
								<SkuAddToList.SubmitButton>加入商品</SkuAddToList.SubmitButton>
							</Stack>
							<SkuAddToList.ScannerDialog title="商品掃描" continuousScan />
							<SkuAddToList.ConfirmDialog />
							<SkuAddToList.List>
								<InventoryProductList headerRow={productClaimHeaderRow} emptyMessage="請掃描商品條碼加入領用清單" height="340px" />
							</SkuAddToList.List>
						</SkuAddToList>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" type="submit" onClick={handleSubmit}>
					確認
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ProductClaimDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	refresh: PropTypes.func,
};

ProductClaimDialog.displayName = "ProductClaimDialog";

export default ProductClaimDialog;
